<template>
	<div class="circuit-details">
		<hero
			color1="var(--v-accent-base)"
			color2="var(--v-accent-base)"
			class="hero"
		>
			{{ circuit.name }}
		</hero>
		<div class="left">
			<!-- Circuit details -->
			<circuit-details-component
				:circuit="circuit"
				class="hide-desktop"
				@snackbar-event="$emit('snackbar-event', $event)"
			></circuit-details-component>

			<!-- Description of the circuit -->
			<v-card class="rounded-lg description-card circuit-details-card">
				<h1>Sobre el circuito</h1>
				<p :class="{ extended: showMore }">{{ circuit.description }}</p>

				<div class="card-actions">
					<v-spacer></v-spacer>
					<v-btn
						color="info"
						text
						class="rounded-lg"
						v-if="!showMore"
						@click="showMore = true"
						>Ver más</v-btn
					>
					<v-btn
						color="info"
						text
						class="rounded-lg"
						v-else
						@click="showMore = false"
						>Ver menos</v-btn
					>
				</div>
			</v-card>

			<races-container
				v-if="circuit.id"
				class="mb-8"
				@snackbar-event="$emit('snackbar-event', $event)"
				@loading="$emit('loading', $event)"
				:circuit="circuit.id"
			></races-container>

			<v-card class="rounded-lg ranking-card">
				<div class="card-header">
					<h1>Ránking</h1>
				</div>
				<div class="card-main">
					<ranking-component
						:loading="loading"
						:ranking="ranking"
						v-if="user.id"
					></ranking-component>
					<p v-else class="text-center">
						<b>Inicie sesión para ver el ránking</b>
					</p>
				</div>
			</v-card>
		</div>

		<div class="right">
			<circuit-details-component
				:circuit="circuit"
				class="hide-mobile"
				@snackbar-event="$emit('snackbar-event', $event)"
			></circuit-details-component>
			<v-card class="rounded-lg circuit-poster-card">
				<h1>Cartel del circuito</h1>
				<v-img
					class="circuit-img"
					:src="getCircuitPoster(circuit.id)"
					alt="Cartel del circuito"
				></v-img>
			</v-card>
		</div>
	</div>
</template>

<script>
	import moment from "moment";
	import axios from "axios";

	import Hero from "../../components/user/circuit-details/Hero";
	import RacesContainer from "../../components/user/RacesContainer";
	import CircuitDetailsComponent from "../../components/user/circuit-details/CircuitDetailsComponent";
	import RankingComponent from "../../components/user/circuit-details/Ranking";

	import gpxRteToJs from "../../utils/parse-gpx-route";

	export default {
		name: "CircuitDetails",
		components: {
			Hero,
			RacesContainer,
			CircuitDetailsComponent,
			RankingComponent,
		},
		data() {
			return {
				circuit: {},
				ranking: [],
				resultsArray: [],
				rules: {
					notEmpty: (v) =>
						v !== undefined || "El archivo no puede estar vacío",
				},
				valid: false,
				loading: false,
				stravaDialog: false,

				privacyAccepted: false,
				showMore: false,

				accepted: [(v) => v || "Debe aceptar para continuar"],
			};
		},
		filters: {
			formatDate(value) {
				return moment(value).locale("es").format("LLL");
			},
			formatTime(secs) {
				return moment.utc(secs * 1000).format("HH:mm:ss");
			},
		},
		computed: {
			user() {
				return this.$store.getters["user/getUser"];
			},
		},
		methods: {
			/**
			 * Returns the url for the user's avatar
			 */
			getUserAvatar(id) {
				return `${process.env.VUE_APP_BACK_URL}/media/avatars/${id}.jpeg`;
			},

			/**
			 * Gets poster
			 */
			getCircuitPoster(id) {
				if (id) {
					return `${process.env.VUE_APP_BACK_URL}/media/circuit-posters/${id}.png`;
				}
			},
			/**
			 * Fetches the circuit
			 */
			async fetchCircuit() {
				this.$emit("loading", true);
				try {
					const response = await axios.get(
						`circuit/${this.$route.query.id}`,
						{}
					);

					this.circuit = response.data;
				} catch (error) {
					//If there is any error, inform the user
					let errorToDisplay;

					//If there is an array of errors
					if (error.response.data instanceof Array) {
						errorToDisplay = "";
						error.response.data.forEach((error) => {
							errorToDisplay += `${error}<br />`;
						});
						//If only one error
					} else if (error.response) {
						errorToDisplay = error.response.data;
						//Rest of the cases
					} else {
						errorToDisplay =
							"Ha ocurrido un error, pruebe más tarde por favor";
					}
					this.$emit("snackbar-event", {
						color: "error",
						text: errorToDisplay,
					});
				} finally {
					this.$emit("loading", false);
				}
			},

			/**
			 * Fetches the ranking
			 */
			async fetchRanking() {
				try {
					const response = await axios.get("/circuit/ranking", {
						params: {
							circuitId: this.circuit.id,
						},
					});
					this.ranking = response.data;
				} catch (error) {
					console.error(error);
				}
			},

			/**
			 * Inscribes the user in the circuit
			 */
			async inscribeInCircuit() {
				try {
					this.$emit("loading", true);

					await axios.post("/user/inscription/circuit", {
						id: this.circuit.id,
					});

					this.$emit("snackbar-event", {
						color: "success",
						text: "Se ha completado la inscripción en el circuito",
					});

					this.circuit.inscribed = true;
				} catch (error) {
					//But if it is not possible, redirect the user to the login
					if (error.response && error.response.status === 401) {
						this.$router.push({
							name: "UserLogin",
							params: {
								redirect: this.$route.fullPath,
							},
						});
					} else {
						//In case the server is not working. show an error
						this.$emit("snackbar-event", {
							color: "error",
							text:
								"Ha ocurrido un error, vuelva a intentarlo más tarde",
						});
					}
				} finally {
					this.$emit("loading", false);
				}
			},

			/**
			 * Processes the uploaded file
			 */
			async resultsUploaded(file) {
				try {
					this.loading = true;
					if (file) {
						this.resultsArray = await gpxRteToJs(file, {
							recordedRoute: true,
						});
						this.valid = true;
					} else {
						this.valid = false;
					}
				} catch (error) {
					this.resultsArray = [];
					this.valid = false;
					this.$emit("snackbar-event", {
						color: "error",
						text: "No se puede procesar el archivo GPX introducido",
					});
				} finally {
					this.loading = false;
				}
			},
		},
		async mounted() {
			this.$vuetify.goTo(0);
			await this.fetchCircuit();
			//await this.fetchRanking();

			if (this.$route.params.checkPayment) {
				if (this.circuit.inscribed) {
					this.$emit("snackbar-event", {
						color: "success",
						text: "Se ha completado el pago correctamente",
					});
				} else {
					this.$emit("snackbar-event", {
						color: "error",
						text:
							"No se ha completado el pago, si cree que es un error, por favor contacte con nosotros",
					});
				}
			}
		},
	};
</script>

<style lang="scss" scoped>
	@import "../../scss/_mixins.scss";
	@import "../../scss/_variables.scss";

	.circuit-details {
		height: 100%;
		width: 100%;

		display: flex;
		flex-wrap: wrap;
		justify-content: space-evenly;

		background-color: #f0f0f3;
		padding: 2em 0.5em 0.5em 0.5em;

		@include desktop() {
			padding: 2em;
		}

		.hero {
			margin-bottom: 2em;
		}

		.circuit-details-card {
			padding: 2em;
			margin-bottom: 2em;

			&.results-margin-helper {
				margin: 0;
			}

			h1 {
				margin-bottom: 0.6em;
			}

			p {
				margin: 0;
				text-align: justify;
				font-size: 1.1em;
				line-height: 1.8em;
			}

			.card-actions {
				margin-top: 2em;
				display: flex;
				flex-wrap: wrap;
			}
		}

		.ranking-card {
			margin-bottom: 2em;

			.card-header {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				margin: 0;
				padding: 2em;
				background-color: var(--v-secondary-base);

				h1 {
					flex: 1;
					text-align: center;
					padding: 0 0.5em;
					color: #fff;
					font-size: 2em;

					@include desktop() {
						font-size: 3em;
					}
				}
			}

			.card-main {
				padding: 2em;
			}
		}

		.left {
			width: 100%;
			display: flex;
			flex-direction: column;

			@include desktop() {
				flex: 5;
			}

			.results-card {
				width: 100%;
				margin-bottom: 2em;

				.privacy-notice {
					background-color: #e6e6ec;
					padding: 2em;
					p {
						text-align: justify;
						font-size: 0.7em;
						margin: 0;
					}
				}

				.results-input {
					margin-top: 1em;
				}

				.card-actions {
					width: 100%;
					margin-top: 2em;
					display: flex;
					flex-wrap: wrap;
					justify-content: flex-end;

					.result-button {
						margin-left: 1em;

						.result-button-image {
							transform: translateY(-0.11em);
						}
					}

					.strava-alert-container {
						width: 100%;

						.strava-alert {
							border: 1px solid #fc4c02;
						}
					}
				}
			}

			.description-card {
				margin-bottom: 2em;

				p {
					white-space: pre-wrap;
					overflow: hidden;
					max-height: 33vh;
					min-height: 0;
					transition: all 0.5s cubic-bezier(0, 1, 0, 1);

					&.extended {
						min-height: 100px;
						max-height: 2500em;
						overflow-y: auto;
						transition: max-height 1s ease-in-out;
					}
				}
			}

			.details-card.hide-desktop {
				margin-bottom: 2em;
			}
		}

		.right {
			width: 100%;
			display: flex;
			flex-direction: column;

			@include desktop() {
				margin-top: 0;
				flex: 3;
				margin-left: 2em;
			}

			.circuit-poster-card {
				padding: 2em;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				min-height: 30vh;
				margin-bottom: 2em;

				h1 {
					align-self: flex-start;
					margin-bottom: 1em;
				}

				circuit-img {
					border-radius: 0;
					border: 1px solid #ccc;
					max-width: 80vw;

					@include desktop() {
						max-width: 500px;
					}
				}
			}
		}
	}

	.strava-activities-card {
		padding: 2em;
	}

	.small {
		font-size: 0.8em !important;

		a {
			font-weight: 700;
		}
	}
</style>

<style lang="scss">
	.ranking-card {
		.v-data-table table tbody td {
			padding: 1em !important;
		}
	}
</style>
